// src/components/admin/scheduling/loggingApi.js
import {
  collection,
  addDoc,
  getDocs,
  query,
  where,
  orderBy,
  limit,
  startAfter,
  doc,
  deleteDoc,
  writeBatch,
  Timestamp,
} from "firebase/firestore";
import { getAuth } from "firebase/auth";

import { db } from "../../../firebaseConfig";

const LOGS_COL = "activity_logs";
const RETENTION_DAYS = 14;
const LOGGING_PREF_PREFIX = "activity_logs_enabled_for_";

function msDays(days) {
  return days * 24 * 60 * 60 * 1000;
}

// Normalize data to ensure we always have strings for the new columns
function normalizeMeta(meta = {}) {
  const m = { ...meta };

  // --- Student ---
  const studentName = (m.studentName || "").toString().trim();
  if (studentName) {
    m.studentName = studentName;
    m.studentNameLower = studentName.toLowerCase();
  } else if (!m.studentNameLower) {
    m.studentNameLower = "";
  }

  // --- Tutor (Handle transitions "A -> B") ---
  const tutorName = (m.tutorName || "").toString().trim();
  if (tutorName) {
    m.tutorName = tutorName;
    m.tutorNameLower = tutorName.toLowerCase();
  } else if (!m.tutorNameLower) {
    m.tutorNameLower = "";
  }

  // --- Context (At) ---
  if (typeof m.sessionContext === "string") {
    m.sessionContext = m.sessionContext.trim();
  } else {
    m.sessionContext = ""; // Prevent null issues in UI
  }

  return m;
}

function currentUserUid() {
  return getAuth().currentUser?.uid || null;
}

function loggingPrefKey(uid) {
  return `${LOGGING_PREF_PREFIX}${uid}`;
}

export function getLoggingEnabledForCurrentUser() {
  try {
    const uid = currentUserUid();
    if (!uid) return true;
    const raw = localStorage.getItem(loggingPrefKey(uid));
    if (raw == null) return true;
    return raw === "1";
  } catch {
    return true;
  }
}

export function setLoggingEnabledForCurrentUser(enabled) {
  try {
    const uid = currentUserUid();
    if (!uid) return;
    localStorage.setItem(loggingPrefKey(uid), enabled ? "1" : "0");
  } catch {
    // no-op
  }
}

export async function logActivity(actionType, description, meta = {}) {
  try {
    if (!getLoggingEnabledForCurrentUser()) {
      return null;
    }

    const payload = {
      actionType,
      description,
      timestamp: Timestamp.now(),
      meta: normalizeMeta(meta),
    };

    const docRef = await addDoc(collection(db, LOGS_COL), payload);

    // Fire-and-forget purge to keep DB clean without blocking UI
    purgeOldLogs().catch((err) => console.warn("Background purge failed", err));

    return docRef.id;
  } catch (err) {
    console.error("Failed to write log", err);
    return null;
  }
}

export async function fetchActivityLogs(lastDoc = null, pageSize = 50, filters = {}) {
  const base = collection(db, LOGS_COL);

  // Note: For strict "Where + OrderBy" in Firestore, you need a composite index.
  // If index is missing, this might throw. 
  // We prioritize sorting by time. Client-side filtering can be a fallback for small datasets,
  // but here we construct the query assuming the user might create the index or we filter lightly.
  
  let constraints = [];

  if (filters?.actionType && filters.actionType !== "ALL") {
    constraints.push(where("actionType", "==", filters.actionType));
  }
  
  constraints.push(orderBy("timestamp", "desc"));
  
  if (lastDoc) {
    constraints.push(startAfter(lastDoc));
  }
  
  constraints.push(limit(pageSize));

  const q = query(base, ...constraints);

  const snap = await getDocs(q);
  const logs = snap.docs.map((d) => ({ id: d.id, ...d.data() }));
  const lastVisible = snap.docs.length ? snap.docs[snap.docs.length - 1] : null;

  return { logs, lastVisible };
}

export async function deleteLogItem(id) {
  if (!id) return;
  await deleteDoc(doc(db, LOGS_COL, id));
}

export async function clearAllLogs() {
  const base = collection(db, LOGS_COL);
  
  // Batched delete loop
  while (true) {
    const snap = await getDocs(query(base, limit(500)));
    if (snap.empty) break;

    const batch = writeBatch(db);
    snap.docs.forEach((d) => batch.delete(d.ref));
    await batch.commit();
  }
}

export async function purgeOldLogs(retentionDays = RETENTION_DAYS) {
  const cutoff = new Date(Date.now() - msDays(retentionDays));
  const cutoffTs = Timestamp.fromDate(cutoff);

  const base = collection(db, LOGS_COL);
  const q = query(base, where("timestamp", "<", cutoffTs), limit(500));

  const snap = await getDocs(q);
  if (snap.empty) return;

  const batch = writeBatch(db);
  snap.docs.forEach((d) => batch.delete(d.ref));
  await batch.commit();
  console.log(`Auto-purged ${snap.size} old logs.`);
}
